import {AiToolImpl} from "@me8eon/aiclient";
import { EnvironmentConfig } from "@me8eon/config";
import {
    ElasticSearchConfig,
} from "@me8eon/ai.tools.utils";
import { WidgetMeta } from "@me8eon/ai.tools.utils/src/widget";
import {JwtTokenFn} from "@me8eon/authentication";

const GRAPH_BACKEND_URL = `${EnvironmentConfig.gatewayURL}/service/graph`;

type ToolArgs = {
    category?: string;
    deviceName?: string;
    query?: string;
}

type ToolImpl = AiToolImpl<ToolArgs>

type Tool = (esConfig: ElasticSearchConfig, jwtTokenFn: JwtTokenFn) => ToolImpl

type DeviceOption = {
    deviceId: string;
    deviceName: string;
    hardwareType?: string;
    lastSessionEvent?: string;
};

const homepageFixWidget = (
    deviceId?: string,
    deviceName?: string,
    devices?: DeviceOption[]
): WidgetMeta<{ deviceId?: string; deviceName?: string; devices?: DeviceOption[] }> => ({
    name: "intune_fix_homepage",
    title: "Browser Homepage Fix",
    description: "Displays a one-click action to enable homepage settings change.",
    type: "widget",
    componentName: "IntuneFixHomepageWidget",
    props: {
        deviceId,
        deviceName,
        devices,
    },
});

export const DisplayIntuneActionToolImpl: Tool = (esConfig, jwtTokenFn) => {
    return {
        type: "intune",
        toolDefn: {
            type: 'function',
            name: "display_intune_action",
            description: "This tool executes specific Intune actions, such as enabling browser homepage management.",
            parameters: {
                type: "object",
                required: [],
                additionalProperties: false,
                properties: {
                    category: {
                        type: "string",
                        description: "The Intune action category.",
                    },
                    query: {
                        type: "string",
                        description: "Optional query used to infer homepage flow.",
                    },
                    deviceName: {
                        type: "string",
                        description: "Optional device name fallback for identification.",
                    }
                }
            },
        },
        execute: async (_indices, {category, query, deviceName}) => {
            try {
                const { axios } = esConfig;

                const queryText = (query || "").toLowerCase();
                const normalizedCategory = (category || "").trim().toLowerCase();
                const inferredHomepage =
                    queryText.includes("homepage") ||
                    queryText.includes("home page") ||
                    queryText.includes("browser") ||
                    queryText.includes("edge") ||
                    queryText.includes("default site") ||
                    queryText.includes("start page") ||
                    queryText.includes("startup page") ||
                    queryText.includes("opens by default") ||
                    queryText.includes("open by default") ||
                    queryText.includes("default page") ||
                    queryText.includes("different page") ||
                    queryText.includes("wrong website") ||
                    queryText.includes("start it") ||
                    queryText.includes("webbläsare") ||
                    queryText.includes("webbläsarens startsida") ||
                    queryText.includes("startsida") ||
                    queryText.includes("standardwebbplats") ||
                    queryText.includes("ändra standardwebbplats") ||
                    queryText.includes("strona startowa") ||
                    queryText.includes("stronę startową") ||
                    queryText.includes("strona główna") ||
                    queryText.includes("stronę główną") ||
                    queryText.includes("domyślna strona") ||
                    queryText.includes("domyślną stronę") ||
                    queryText.includes("przeglądarka") ||
                    queryText.includes("przeglądarce");
                const shouldRunHomepageFlow = normalizedCategory === "homepage-change" || inferredHomepage || !normalizedCategory;

                if (!shouldRunHomepageFlow) {
                    return {
                        value: "I found an Intune action for this request."
                    };
                }

                const token = await jwtTokenFn();
                const headers = {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token || ""}`,
                };

                const resolveUrl = `${GRAPH_BACKEND_URL}/resolve-device-id`;
                const resolvePayload = deviceName ? { deviceName } : {};

                const response = await axios.post(resolveUrl, resolvePayload, { headers });

                if (response?.data?.success && Array.isArray(response?.data?.devices) && response.data.devices.length === 1) {
                    const [identifiedDevice] = response.data.devices;
                    return {
                        _meta: {
                            widget: homepageFixWidget(
                                identifiedDevice.deviceId,
                                identifiedDevice.deviceName,
                                response.data.devices
                            ),
                            skipAiSummary: true,
                        },
                        value: "I identified your device. Click \"fix on this\" to continue."
                    };
                }

                if (response?.data?.success && Array.isArray(response?.data?.devices) && response.data.devices.length > 1) {
                    return {
                        _meta: {
                            widget: homepageFixWidget(undefined, undefined, response.data.devices),
                            skipAiSummary: true,
                        },
                        value: "I found multiple devices. Please select one device and click \"fix this\"."
                    };
                }

                if (response?.data?.success && response?.data?.deviceId) {
                    const identifiedDeviceName = response?.data?.deviceName || deviceName || undefined;
                    const identifiedDeviceId = response?.data?.deviceId;
                    return {
                        _meta: {
                            widget: homepageFixWidget(identifiedDeviceId, identifiedDeviceName),
                            skipAiSummary: true,
                        },
                        value: "I identified your device. Click \"fix this\" to continue."
                    };
                }
                const resolveError = response?.data?.detail || response?.data?.error || "resolve-device-id returned no device";
                console.warn("Intune homepage flow skipped, no device resolved:", resolveError);
                return {
                    _meta: {
                        selectedIndex: "none",
                        skipIntune: true,
                    },
                    value: "No Intune-managed device is available for this user. Do not offer the Intune homepage action. Use knowledge search as fallback."
                };
            } catch (error) {
                console.error("Error executing DisplayIntuneActionToolImpl:", error);
                const backendError = error as {
                    message?: string;
                    response?: {
                        status?: number;
                        data?: {
                            error?: string;
                            detail?: string;
                            message?: string;
                        };
                    };
                };
                const status = backendError.response?.status;
                const detail = backendError.response?.data?.detail || backendError.response?.data?.error || backendError.response?.data?.message || backendError.message || "Unknown error";
                const errorText = status ? `resolve-device-id failed (${status}): ${detail}` : `resolve-device-id failed: ${detail}`;
                return {
                    _meta: {
                        selectedIndex: "none",
                        skipIntune: true,
                    },
                    value: `Intune device lookup is unavailable (${errorText}). Do not offer the Intune homepage action. Use knowledge search as fallback.`
                }
            }
        }
    }
}
